import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ReserverBureauDto} from '../../reserver-un-bureau/model/reserver-bureau.dto';
import {ReservationDto} from '../dto/reservation.dto';
import {ZoneDeTravailAvecIlotsDto} from '../dto/zone-de-travail-avec-ilots.dto';
import {ZoneDeTravailAvecIlotDto} from '../dto/zone-de-travail-avec-ilot.dto';
import {BureauPreferentielDto} from '../dto/bureau-preferentiel.dto';
import {BureauPreferentielOccupationDto} from '../dto/bureau-preferentiel-occupation.dto';
import {UtilisateurAvecBureauDto} from '../../planning/model/utilisateur-avec-bureau.dto';

@Injectable({
	providedIn: 'root'
})
export class ReservationService {

	private urlReservations = '/ws/reservations';
	private urlIlots = '/ws/ilots';

	constructor(private http: HttpClient) {
	}

	/**
	 * Récupère les ilots et les bureaux pour la date de réservation.
	 *
	 * @param zoneId l'id de la zone où récupérer les ilots et bureaux
	 * @param dateReservation la date de réservation.
	 */
	recupererIlotsEtBureauxPourDateReservation(zoneId: number, dateReservation: string): Observable<ZoneDeTravailAvecIlotsDto> {
		const params = new HttpParams()
			.set('use-case', 'reserver-un-bureau')
			.set('zoneId', zoneId)
			.set('dateReservation', dateReservation);
		return this.http.get<ZoneDeTravailAvecIlotsDto>(this.urlIlots, {params: params});
	}

	/**
	 * Réserve un bureau
	 *
	 * @param reserverBureauDto le dto pour réserver un bureau
	 */
	reserverBureau(reserverBureauDto: ReserverBureauDto): Observable<void> {
		return this.http.post<void>(this.urlReservations, reserverBureauDto);
	}

	/**
	 * Récupère l'ilot avec les réservations pour le nom de l'ilot et la date de réservation.
	 *
	 * @param ilotId l'id de l'ilot.
	 * @param dateReservation la date de réservation
	 */
	recupererIlotPourIlotId(ilotId: number, dateReservation: string): Observable<ZoneDeTravailAvecIlotDto> {
		const params = new HttpParams()
			.set('use-case', 'visualiser-reservation')
			.set('ilotId', ilotId)
			.set('dateReservation', dateReservation);
		return this.http.get<ZoneDeTravailAvecIlotDto>(this.urlIlots, {params: params});
	}

	/**
	 * Récupère les réservations d'un occupant pour une période.
	 *
	 * @param dateDebut la date de début de la période.
	 * @param dateFin la date de fin de la période.
	 * @param loginOccupant le login de l'occupant concerné.
	 */
	recupererReservationPourPeriodeEtOccupant(dateDebut: string, dateFin: string, loginOccupant: string): Observable<ReservationDto[]> {
		const params = new HttpParams()
			.set('dateDebut', dateDebut)
			.set('dateFin', dateFin)
			.set('loginOccupant', loginOccupant);
		return this.http.get<ReservationDto[]>(this.urlReservations, {params: params});
	}

	/**
	 * Annule une réservation en la supprimant en BDD.
	 *
	 * @param reservationId l'id de la réservation
	 */
	annulerUneReservation(reservationId: number): Observable<void> {
		const params = new HttpParams()
			.set('reservationId', reservationId);
		return this.http.delete<void>(this.urlReservations, {params: params});
	}

	/**
	 * Recupère les journées où l'utilisateur a un bureau préférentiel et n'a pas besoin de réserver.
	 *
	 * @param dateDebut la date de début de la période.
	 * @param dateFin la date de fin de la période.
	 * @param loginOccupant le login de l'occupant concerné.
	 */
	recupererJourneeAvecPlacePreferentiellePourPeriodeEtOccupant(dateDebut: string, dateFin: string, loginOccupant: string): Observable<BureauPreferentielOccupationDto[]> {
		const params = new HttpParams()
			.set('dateDebut', dateDebut)
			.set('dateFin', dateFin)
			.set('loginOccupant', loginOccupant)
			.set('use-case', 'bureau-preferentiel-pour-periode');
		return this.http.get<BureauPreferentielOccupationDto[]>(this.urlReservations, {params: params});
	}

	/**
	 * Recupère l'id du bureau préférentiel de l'utilisateur, null s'il n'en possède pas.
	 *
	 * @param loginOccupant le login de l'utilisateur.
	 */
	recupererBureauPreferentielUtilisateurConnecte(loginOccupant: string): Observable<BureauPreferentielDto | null> {
		const params = new HttpParams()
			.set('loginOccupant', loginOccupant)
			.set('use-case', 'bureau-preferentiel');
		return this.http.get<BureauPreferentielDto | null>(this.urlReservations, {params: params});
	}

	recupererReservationPourPeriodeEtOccupantsBureauPreferentielInclus(dateDebut: string, dateFin: string, loginsOccupants: string[]): Observable<UtilisateurAvecBureauDto[]> {
		const loginsParams = loginsOccupants.join(',');

		const params = new HttpParams()
			.set('dateDebut', dateDebut)
			.set('dateFin', dateFin)
			.set('loginsOccupants', loginsParams)
			.set('use-case', 'reservations-bureau-preferentiel-inclus');
		return this.http.get<UtilisateurAvecBureauDto[]>(this.urlReservations, {params: params});
	}
}
